/*

Tableau query plans (e.g., logical queries)
--------------------------

Tableau's query plans are stored as XML. We first load that XML using xml2js
and convert it literally into a query tree using `convertJSON`. The XML
already provides us the structure of the rendered tree.

*/
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { typesafeXMLParse } from "./xml";
function normalizeLogicalOperator(tag, clazz) {
    // Logical queries
    // There are two representations:
    //   <joinOp class='logical-operator' comparison='equal-to' join='inner' join-constraints='pk-fk'>
    // and
    //   <logical-operator class='join' comparison='equal-to' join='left' join-constraints='none'>
    if (tag == "logical-operator") {
        return clazz;
    }
    if (clazz == "logical-operator" && tag.endsWith("Op")) {
        return tag.substring(0, tag.length - 2);
    }
    return undefined;
}
function normalizeLogicalExpression(tag, clazz) {
    if (tag == "logical-expression") {
        return clazz;
    }
    if (clazz == "logical-expression" && tag.endsWith("Exp")) {
        return tag.substring(0, tag.length - 3);
    }
    return undefined;
}
function extractProperty(properties, key) {
    var value = properties.get(key);
    properties.delete(key);
    return value;
}
function getNodeRenderingConfig(tag, properties) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    switch (tag) {
        case "join": {
            var joinType = properties.get("join");
            var joinIcons = {
                inner: "inner-join-symbol",
                outer: "full-join-symbol",
                left: "left-join-symbol",
                right: "right-join-symbol",
            };
            var icon = (_a = joinIcons[joinType !== null && joinType !== void 0 ? joinType : ""]) !== null && _a !== void 0 ? _a : "inner-join-symbol";
            return { displayName: "".concat(joinType, " join"), icon: icon };
        }
        case "aggregate":
            return { displayName: tag, icon: "groupby-symbol" };
        case "relation":
            return { displayName: (_b = properties.get("name")) !== null && _b !== void 0 ? _b : tag, icon: "table-symbol" };
        case "tuples":
            return { displayName: (_c = properties.get("alias")) !== null && _c !== void 0 ? _c : tag, icon: "table-symbol" };
        case "select":
            return { displayName: tag, icon: "filter-symbol" };
        case "runquery":
            return { displayName: tag, icon: "run-query-symbol" };
        case "fed-op": {
            var clazz = properties.get("class");
            switch (clazz) {
                case "createtemptable":
                case "createtemptablefromquery":
                case "createtemptablefromtuples":
                    return { displayName: (_d = properties.get("table")) !== null && _d !== void 0 ? _d : clazz, icon: "temp-table-symbol" };
                case "runquery":
                    return { displayName: tag, icon: "run-query-symbol" };
            }
            break;
        }
        case "field":
            return { displayName: (_f = (_e = extractProperty(properties, "~text")) !== null && _e !== void 0 ? _e : extractProperty(properties, "name")) !== null && _f !== void 0 ? _f : tag };
        case "identifier":
            return { displayName: (_g = extractProperty(properties, "identifier")) !== null && _g !== void 0 ? _g : tag };
        case "funcallExp":
            return { displayName: (_h = extractProperty(properties, "function")) !== null && _h !== void 0 ? _h : tag };
        case "literal":
            return { displayName: (properties === null || properties === void 0 ? void 0 : properties.get("datatype")) + ":" + (properties === null || properties === void 0 ? void 0 : properties.get("value")) };
        case "referenceExp":
            return { displayName: "ref:" + (properties === null || properties === void 0 ? void 0 : properties.get("ref")) };
        case "condition":
            return { displayName: (_j = extractProperty(properties, "op")) !== null && _j !== void 0 ? _j : tag };
        case "binding":
            return { displayName: (_l = (_k = extractProperty(properties, "name")) !== null && _k !== void 0 ? _k : extractProperty(properties, "ref")) !== null && _l !== void 0 ? _l : tag };
        case "calculation":
            return { displayName: (_m = extractProperty(properties, "formula")) !== null && _m !== void 0 ? _m : tag };
    }
    return { displayName: tag };
}
function isAlwaysExpanded(xml) {
    var _a;
    var tag = xml["#name"];
    var childLogicalOp = normalizeLogicalOperator(xml["#name"], (_a = xml.$) === null || _a === void 0 ? void 0 : _a.class) !== undefined;
    return childLogicalOp || tag == "fed-op" || tag == "logical-query";
}
// Convert JSON as returned by xml2js parser to d3 tree format
function convertXML(xml) {
    var e_1, _a, e_2, _b;
    var _c, _d, _e;
    var tag = xml["#name"];
    // The properties
    var properties = new Map();
    var text = (_c = xml._) === null || _c === void 0 ? void 0 : _c.trim();
    if (text)
        properties.set("~text", text);
    if (xml.$) {
        try {
            for (var _f = __values(Object.getOwnPropertyNames(xml.$)), _g = _f.next(); !_g.done; _g = _f.next()) {
                var key = _g.value;
                properties.set(key, xml.$[key]);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    // Normalize the representation of logical operators
    var operatorType = normalizeLogicalOperator(tag, properties.get("class"));
    var exprType = normalizeLogicalExpression(tag, properties.get("class"));
    if (operatorType) {
        properties.delete("class");
        tag = operatorType;
    }
    else if (exprType) {
        properties.delete("class");
        tag = exprType;
    }
    var nodeRendering = getNodeRenderingConfig(tag, properties);
    var name = (_d = nodeRendering.displayName) !== null && _d !== void 0 ? _d : tag;
    var icon = nodeRendering.icon;
    var expandedChildren = [];
    var collapsedChildren = [];
    try {
        for (var _h = __values((_e = xml.$$) !== null && _e !== void 0 ? _e : []), _j = _h.next(); !_j.done; _j = _h.next()) {
            var child = _j.value;
            var children = isAlwaysExpanded(child) ? expandedChildren : collapsedChildren;
            children.push(convertXML(child));
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_j && !_j.done && (_b = _h.return)) _b.call(_h);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return {
        name: name,
        icon: icon,
        properties: properties,
        children: expandedChildren,
        collapsedChildren: collapsedChildren,
        expandedByDefault: !isAlwaysExpanded(xml) && expandedChildren.length == 0,
    };
}
export function loadTableauPlan(graphString) {
    var xml = typesafeXMLParse(graphString);
    var root = convertXML(xml);
    return { root: root, crosslinks: undefined };
}
